import axios from 'axios';
import { attachAxiosInterceptor } from '../shared/errorReporting';
import { CENTRAL_AUTH_ORIGIN } from '../utils/sso';

type RequestOptions = {
  signal?: AbortSignal;
  // When true, expected/handled failures (e.g. a 403 the UI shows its own
  // message for) are skipped by the global error reporter (no red toast /
  // console.error). The browser's own network log line is unavoidable.
  suppressGlobalError?: boolean;
};

const API_BASE_URL = (() => {
  if (typeof window !== 'undefined') {
    return `${window.location.origin}/api`;
  }
  return 'http://localhost:5001/api';
})();

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

attachAxiosInterceptor(apiClient);

const LOGIN_REDIRECT_GUARD_KEY = 'letters_login_redirect_guard';
const AUTH_COOKIE_SUFFIX = import.meta.env.VITE_AUTH_COOKIE_SUFFIX || '';
const AUTH_TOKEN_COOKIE = `spm_token${AUTH_COOKIE_SUFFIX}`;
const AUTH_USER_COOKIE = `spm_user${AUTH_COOKIE_SUFFIX}`;

function isJwtExpired(token: string): boolean {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    const payload = JSON.parse(atob(parts[1]));
    const exp = Number(payload?.exp || 0);
    if (!exp) return false;
    return Date.now() >= exp * 1000;
  } catch {
    return true;
  }
}

function clearCookie(name: string) {
  if (typeof document === 'undefined') return;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax${secure}`;
  document.cookie = `${name}=; Path=/; Domain=.surbhi.net; Max-Age=0; SameSite=Lax${secure}`;
}

function clearAuthState() {
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }
  clearCookie(AUTH_TOKEN_COOKIE);
  clearCookie(AUTH_USER_COOKIE);
}

function buildLoginRedirectUrl(): string {
  const next =
    typeof window !== 'undefined'
      ? window.location.href
      : `${(import.meta.env.VITE_LETTERS_PUBLIC_ORIGIN as string) || 'https://letter.surbhi.net'}/`;
  return `${CENTRAL_AUTH_ORIGIN}/login?next=${encodeURIComponent(next)}`;
}

function shouldRedirectToLogin(): boolean {
  if (typeof window === 'undefined') return false;
  const now = Date.now();
  const currentUrl = window.location.href;

  try {
    const raw = sessionStorage.getItem(LOGIN_REDIRECT_GUARD_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { at?: number; url?: string };
      const lastAt = Number(parsed?.at || 0);
      const lastUrl = String(parsed?.url || '');
      if (lastUrl === currentUrl && now - lastAt < 15000) {
        return false;
      }
    }
  } catch {
    // Ignore guard parse failures and overwrite with fresh value.
  }

  sessionStorage.setItem(LOGIN_REDIRECT_GUARD_KEY, JSON.stringify({ at: now, url: currentUrl }));
  return true;
}

function getCookieValue(name: string): string | null {
  return getCookieValues(name)[0] || null;
}

function getCookieValues(name: string): string[] {
  if (typeof document === 'undefined') return [];
  return document.cookie
    .split('; ')
    .filter((row) => row.startsWith(`${name}=`))
    .map((cookie) => decodeURIComponent(cookie.split('=').slice(1).join('=')))
    .filter(Boolean);
}

function getAuthToken(): string | null {
  const cookieToken =
    getCookieValues(AUTH_TOKEN_COOKIE).find((token) => !isJwtExpired(token)) || '';
  const localToken = localStorage.getItem('token') || '';

  if (cookieToken && !isJwtExpired(cookieToken)) {
    if (localToken !== cookieToken) localStorage.setItem('token', cookieToken);
    return cookieToken;
  }

  if (localToken && !isJwtExpired(localToken)) {
    return localToken;
  }

  if (cookieToken || localToken) {
    clearAuthState();
  }

  return null;
}

function isProtectedLettersEndpoint(url: string): boolean {
  return url.includes('/letters') && !url.includes('/letters/qr/verify/');
}

apiClient.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const isOnLoginPage = window.location.pathname === '/login';
      const endpoint = String(error.config?.url || '');
      if (!isOnLoginPage && isProtectedLettersEndpoint(endpoint) && shouldRedirectToLogin()) {
        clearAuthState();
        window.location.href = buildLoginRedirectUrl();
      }
    }
    return Promise.reject(error);
  },
);

export const lettersApi = {
  getTemplate: () => apiClient.get('/letters/template'),
  listTemplates: () => apiClient.get('/letters/templates'),
  getTemplateById: (templateId: number | string) =>
    apiClient.get(`/letters/templates/${templateId}`),
  createTemplate: (payload: any) => apiClient.post('/letters/templates', payload),
  updateTemplate: (templateId: number | string, payload: any) =>
    apiClient.put(`/letters/templates/${templateId}`, payload),
  getStats: (options?: RequestOptions) =>
    apiClient.get('/letters/stats', { signal: options?.signal }),
  list: (params?: any) => apiClient.get('/letters', { params }),
  checkName: (params: { fileName?: string; templateName?: string; excludeId?: number | string }) =>
    apiClient.get('/letters/check-name', { params }),
  get: (id: number | string, options?: RequestOptions) =>
    apiClient.get(`/letters/${id}`, {
      signal: options?.signal,
      suppressGlobalError: options?.suppressGlobalError,
    } as any),
  create: (payload: any) => apiClient.post('/letters', payload),
  update: (id: number | string, payload: any) => apiClient.put(`/letters/${id}`, payload),
  uploadMappedPdf: (id: number | string, file: File) => {
    const formData = new FormData();
    formData.append('pdf', file);
    return apiClient.post(`/letters/${id}/mapped-pdf`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  listUploads: (id: number | string, options?: RequestOptions) =>
    apiClient.get(`/letters/${id}/uploads`, {
      suppressGlobalError: options?.suppressGlobalError,
    } as any),
  downloadUpload: (id: number | string, uploadId: number | string) =>
    apiClient.get(`/letters/${id}/uploads/${uploadId}`, { responseType: 'blob' }),
  downloadMappedPdf: (id: number | string) =>
    apiClient.get(`/letters/${id}/mapped-pdf`, { responseType: 'blob' }),
  delete: (id: number | string) => apiClient.delete(`/letters/${id}`),
  downloadDocx: (id: number | string) =>
    apiClient.get(`/letters/${id}/download/docx`, { responseType: 'blob' }),
  listSignatoryOptions: (options?: RequestOptions) =>
    apiClient.get('/letters/signatory-options', { signal: options?.signal }),
  listPendingApproval: (
    params?: { filter?: 'pending_with_you' | 'requested_by_you' | 'rejected_letters' },
    options?: RequestOptions,
  ) => apiClient.get('/letters/pending-approval', { params, signal: options?.signal }),
  approve: (id: number | string) => apiClient.post(`/letters/${id}/approve`),
  reject: (id: number | string, payload: { remark: string }) =>
    apiClient.post(`/letters/${id}/reject`, payload),
  /** Creator pulls a pending-approval letter back to draft so it can be edited and resubmitted. */
  recall: (id: number | string) => apiClient.post(`/letters/${id}/recall`),
  getAuditLogs: (id: number | string) => apiClient.get(`/letters/${id}/audit-logs`),
  listRoleAssignments: (options?: RequestOptions) =>
    apiClient.get('/letters/admin/roles', { signal: options?.signal }),
  updateRoleAssignment: (
    userId: number | string,
    payload: { is_admin?: boolean; is_authorized_signatory?: boolean },
  ) => apiClient.put(`/letters/admin/roles/${userId}`, payload),
  listAuthorizedSignatories: (options?: RequestOptions) =>
    apiClient.get('/letters/admin/signatories', { signal: options?.signal }),
  listSignatureManagementUsers: (options?: RequestOptions) =>
    apiClient.get('/letters/admin/signature-management-users', { signal: options?.signal }),
  getSignatorySignature: (userId: number | string, options?: RequestOptions) =>
    apiClient.get(`/letters/admin/signatories/${userId}/signature`, { signal: options?.signal }),
  saveSignatorySignature: (
    userId: number | string,
    payload: {
      signer_name: string;
      designation: string;
      details_text: string;
      signature_html: string;
    },
  ) => apiClient.post(`/letters/admin/signatories/${userId}/signature`, payload),
  deleteSignatorySignature: (userId: number | string) =>
    apiClient.delete(`/letters/admin/signatories/${userId}/signature`),
  verifyQRToken: (token: string) => apiClient.get(`/letters/qr/verify/${token}`),
  getAccessLogs: (letterId: number | string) => apiClient.get(`/letters/${letterId}/access-logs`),
};
